import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './MessageActions.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * The row of bare glyphs that hangs under a message.
 *
 * Four extraction passes proposed it as two components — `MessageActionsRow` / `MessageActionBar`
 * for the assistant, a separate hover `MessageActionBar` for the user. In the file it is one row
 * with one job (act on the message above it) drawn at two densities, so it is one component and
 * `side` is the discriminator. Keeping it one is what guarantees the two sides never drift apart
 * on tone, hit box or focus ring — the only things that legitimately differ are how many glyphs
 * there are, how big they are, and how far apart.
 *
 * `assistant` — four 16px glyphs at gap 14, on all eight boards that end a turn (*Done*,
 * *Founder · cold start*, *Designer · edit loop* twice, *Engineer · research to document*,
 * *Manager · one shot*, *Task reports back*, *Answer, nothing made*), always in the order
 * copy - thumbs-up - thumbs-down - retry.
 * `user` — two 15px glyphs at gap 12, revealed on hover (*Hover*).
 *
 * **Every handler is the switch for its own glyph.** The artboards always draw the full set —
 * conversation.md's closing rule is "ActionRow is constant: gap 14px, exactly four 16px glyphs at
 * hint, in the order copy · thumbs-up · thumbs-down · retry", and *Hover* always draws both user
 * glyphs — so a caller that passes every handler gets exactly what Paper drew and nothing here
 * changes. What the design has no drawing for is a glyph that does nothing when you click it, and
 * that is what an unhandled button was: `side="user"` with only `onCopy` used to render a live
 * Copy beside a dead Edit. A button is now drawn when, and only when, there is something behind
 * it. With no handlers at all there is no row: the component renders `null` rather than an empty
 * `role="group"` that would still eat a gap in its parent's column.
 */
export type MessageActionsProps = { theme?: ChatTheme; className?: string } & (
  | {
      side: 'assistant';
      onCopy?: () => void;
      onRateUp?: () => void;
      onRateDown?: () => void;
      onRetry?: () => void;
    }
  | {
      side: 'user';
      onCopy?: () => void;
      onEdit?: () => void;
      /**
       * Hover reveal. *Hover*'s whole subject is that these two appear and the bubble does not
       * change, so the row fades rather than being mounted and unmounted — a row that enters the
       * DOM on `mouseenter` cannot animate, and it drops out of the tab order at the moment a
       * keyboard user reaches for it.
       *
       * Hidden means `opacity: 0` + `pointer-events: none`, NOT `visibility: hidden`. The space
       * stays reserved either way, but `visibility` also takes the two buttons out of the tab
       * order — and in the composite that owns this row hover is the ONLY way to reveal it, so
       * unfocusable-while-hidden means a keyboard user can never reach copy or edit at all. The
       * buttons therefore stay focusable while faded, and the row reveals itself on
       * `:focus-within` so Tab landing on Copy brings the pair into view around it.
       */
      visible?: boolean;
    }
);

/**
 * Registry name + accessible name per action.
 *
 * The names are fixed rather than props: the order of the assistant glyphs is not a prop (the
 * design fixes it), and neither is what each one does, so a caller who could rename "Retry" could
 * only ever make it lie. `Copy` carries a capital C — the registry keys are the SVG filenames and
 * this one is `Copy.svg`, so `copy` silently renders nothing.
 */
const ACTIONS = {
  copy: { icon: 'Copy', label: 'Copy message' },
  rateUp: { icon: 'like', label: 'Good response' },
  rateDown: { icon: 'dislike', label: 'Bad response' },
  // One registry file, three roles across the design — `loop-right` is the running spinner at
  // subtle, the deleting spinner at hint, and this retry. Same two arcs every time.
  retry: { icon: 'loop-right', label: 'Retry' },
  edit: { icon: 'edit', label: 'Edit message' },
} as const;

type ActionKey = keyof typeof ACTIONS;

/**
 * One drawn glyph. `onClick` is required rather than optional, which is the type-level half of the
 * dead-button fix: a glyph cannot be assembled without the thing it does.
 */
type Glyph = { action: ActionKey; size: 15 | 16; onClick: () => void };

function ActionButton({ action, size, onClick }: Glyph) {
  const { icon, label } = ACTIONS[action];
  return (
    <button
      type="button"
      className={styles.action}
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      {/* Raw, not `ActivityGlyph`. These four are the content of their buttons — the mark IS the
          verb, and it is the button's only visible name, which is why each one carries an
          `aria-label` and none of them is decorative. `chrome` is the axis for what a control says
          about itself (opens, closes, ticked); "copy this message" is not that. */}
      <DeckIcon name={icon} size={size} />
    </button>
  );
}

export const MessageActions = React.forwardRef<HTMLDivElement, MessageActionsProps>(
  function MessageActions(props, ref) {
    const { theme: themeProp, className } = props;
    const theme = useV15Theme(themeProp);

    // Narrowed off `props` so the union stays honest without a cast.
    const isUser = props.side === 'user';
    const visible = props.side === 'user' ? props.visible ?? true : true;

    // Built as a list rather than branched inline so the fixed order survives the filtering: the
    // design fixes copy - thumbs-up - thumbs-down - retry, and dropping the middle two must close
    // the gap, not leave a hole. Push order IS the artboard order.
    const glyphs: Glyph[] = [];
    if (props.side === 'assistant') {
      // Destructured inside the narrowed branch so each handler is a plain `(() => void) |
      // undefined` and the `if` narrows it for the push — `props.onCopy` would widen again on
      // every access and force a non-null assertion to satisfy `Glyph`.
      const { onCopy, onRateUp, onRateDown, onRetry } = props;
      if (onCopy) glyphs.push({ action: 'copy', size: 16, onClick: onCopy });
      if (onRateUp) glyphs.push({ action: 'rateUp', size: 16, onClick: onRateUp });
      if (onRateDown) glyphs.push({ action: 'rateDown', size: 16, onClick: onRateDown });
      if (onRetry) glyphs.push({ action: 'retry', size: 16, onClick: onRetry });
    } else {
      const { onCopy, onEdit } = props;
      if (onCopy) glyphs.push({ action: 'copy', size: 15, onClick: onCopy });
      if (onEdit) glyphs.push({ action: 'edit', size: 15, onClick: onEdit });
    }

    // Nothing to act with, so nothing to draw. An empty row would still be a flex item of the
    // 12px conversation column and a labelled group in the accessibility tree — a gap and an
    // announcement for a control set that does not exist.
    if (glyphs.length === 0) {
      return null;
    }

    return (
      <div
        ref={ref}
        // A group rather than a toolbar: `role="toolbar"` promises arrow-key roving focus, and
        // two-to-four adjacent buttons are cheaper to tab through than to teach.
        role="group"
        aria-label={isUser ? 'Message actions' : 'Response actions'}
        className={cn(
          chatThemeClass(theme),
          styles.row,
          isUser ? styles.user : styles.assistant,
          isUser && !visible && styles.hidden,
          className
        )}
      >
        {glyphs.map((glyph) => (
          <ActionButton
            key={glyph.action}
            action={glyph.action}
            size={glyph.size}
            onClick={glyph.onClick}
          />
        ))}
      </div>
    );
  }
);
